// Free Transform (Ctrl+T) — Photoshop-style scale + rotate around the active
// raster layer's bounding box.
//
// Lifecycle:
//   begin(ctx)                 — enter active mode. Captures the layer's
//                                pre-transform state (its pixel buffer and
//                                position). The host wires Ctrl+T to call
//                                this; pressing T alone selects the tool.
//   onPointerDown/Move/Up      — drag handles. Each drag move emits a
//                                transient TransformLayerCommand so the
//                                renderer can preview.
//   commit()                   — emit the final TransformLayerCommand
//                                (records in history) and exit active mode.
//   cancel()                   — restore the pre-transform buffer/position
//                                via an inverse transform and exit.
//
// The tool tracks a "current matrix" that maps source-buffer coords → the
// transformed output-buffer's local coords. On each emit we rebuild the
// output buffer dimensions from the transformed bounding box of the
// original layer rect, then translate the matrix so the output origin is
// at (0, 0). The layer's (x, y) absorbs the global translation.
//
// v2 additions (issue #190):
//   - Skew mode: dragging an edge handle shears along that edge.
//   - Distort mode: dragging any corner moves it freely (no symmetry).
//   - Perspective mode: dragging a corner moves the diagonally-opposite
//     corner symmetrically (preserves vanishing-point feel).
//   - Full mesh Warp is still out of scope.
//   - Handle hit-testing uses simple zones (corners / edges / inside /
//     outside the bbox).

import type { Command } from "../commands/Command";
import { TransformLayerCommand, type OriginalLayerSnapshot } from "../commands/TransformLayerCommand";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Affine2D } from "../pixel/affineTransform";
import {
  compose,
  identity,
  rotate as rotateMat,
  scale as scaleMat,
  translate as translateMat,
} from "../pixel/affineTransform";
import { screenToDoc } from "../render/Viewport";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";

export type RefPoint =
  | "top-left" | "top" | "top-right"
  | "left" | "center" | "right"
  | "bottom-left" | "bottom" | "bottom-right";

/**
 * Selectable transform mode (Photoshop Edit > Transform submenu — audit i23).
 * "free" is the default Ctrl+T behaviour (corner drag scales, edge drag
 * scales one axis, outside drag rotates, inside drag moves). "scale" and
 * "rotate" restrict the same box to just that one gesture, matching
 * Photoshop's Transform > Scale / Transform > Rotate menu items.
 */
export type FreeTransformMode = "free" | "scale" | "rotate" | "skew" | "distort" | "perspective";

type HandleZone =
  | "corner-tl"
  | "corner-tr"
  | "corner-bl"
  | "corner-br"
  | "edge-t"
  | "edge-b"
  | "edge-l"
  | "edge-r"
  | "inside"
  | "outside";

/** Per-corner offsets used by Distort / Perspective modes (doc-space). */
interface CornerOffsets {
  tl: { dx: number; dy: number };
  tr: { dx: number; dy: number };
  bl: { dx: number; dy: number };
  br: { dx: number; dy: number };
}

interface ActiveState {
  // Layer being transformed.
  readonly layerId: string;
  // The layer's state at begin() — what cancel() must restore to.
  readonly srcWidth: number;
  readonly srcHeight: number;
  readonly srcX: number;
  readonly srcY: number;
  // Frozen clone of the layer's pixel buffer at begin() (audit i20). Every
  // transient/commit command built during this session transforms FROM this
  // buffer, never from whatever the doc's live layer currently holds — so
  // repeated drags don't compound on top of the previous move's result.
  readonly srcBuffer: PixelBuffer;
  // The current "in-progress" transform parameters. We re-derive the matrix
  // from these on every drag so dragging a corner from scale 2 back to 1
  // round-trips cleanly.
  scaleX: number;
  scaleY: number;
  rotation: number; // radians
  translateX: number; // document-space dx applied after centring/scaling/rotation
  translateY: number;
  // Shear (skew mode only) — applied after scale before rotation.
  shearX: number; // x-shear coefficient
  shearY: number; // y-shear coefficient
  // Per-corner offsets (distort / perspective modes).
  cornerOffsets: CornerOffsets;
  // Drag bookkeeping.
  dragging: {
    zone: HandleZone;
    startDocX: number;
    startDocY: number;
    startScaleX: number;
    startScaleY: number;
    startRotation: number;
    startTranslateX: number;
    startTranslateY: number;
    startShearX: number;
    startShearY: number;
    startCornerOffsets: CornerOffsets;
    startAltKey: boolean;
  } | null;
}

export class FreeTransformTool implements Tool {
  readonly id = "free-transform";
  readonly shortcut = "T";

  // Transform reference point (pivot). Default is center.
  refPoint: RefPoint = "center";

  /**
   * Current transform mode. Can be changed programmatically (e.g. from a
   * context-menu handler in the host) while the tool is active.
   * "free" = standard scale/rotate (the original v1 behaviour).
   */
  mode: FreeTransformMode = "free";

  /**
   * When true (post-CC2019 default), plain corner drag preserves the original
   * aspect ratio; Shift toggles it free. When false (pre-CC2019 legacy), plain
   * corner drag is free and Shift locks aspect ratio.
   */
  aspectLockByDefault = true;

  private state: ActiveState | null = null;

  // Change-notification subscribers (for ToolUX options-bar).
  private _changeListeners: (() => void)[] = [];

  // --- Change-notification API (ToolUX subscribes) -------------------------

  /** Register a callback fired whenever any transform parameter changes. */
  onTransformChange(cb: () => void): void {
    if (!this._changeListeners.includes(cb)) this._changeListeners.push(cb);
  }

  /** Unregister a previously-registered callback. */
  offTransformChange(cb: () => void): void {
    const idx = this._changeListeners.indexOf(cb);
    if (idx !== -1) this._changeListeners.splice(idx, 1);
  }

  private _notifyChange(): void {
    for (const cb of this._changeListeners) cb();
  }

  // --- Live transform parameter accessors (for the options-bar fields) ------

  /** Scale X as percentage (100 = no scale). */
  get scaleXPct(): number {
    return (this.state?.scaleX ?? 1) * 100;
  }
  set scaleXPct(pct: number) {
    if (!this.state) return;
    this.state.scaleX = pct / 100;
    this._notifyChange();
  }

  /** Scale Y as percentage (100 = no scale). */
  get scaleYPct(): number {
    return (this.state?.scaleY ?? 1) * 100;
  }
  set scaleYPct(pct: number) {
    if (!this.state) return;
    this.state.scaleY = pct / 100;
    this._notifyChange();
  }

  /** Rotation in degrees. */
  get rotationDeg(): number {
    return ((this.state?.rotation ?? 0) * 180) / Math.PI;
  }
  set rotationDeg(deg: number) {
    if (!this.state) return;
    this.state.rotation = (deg * Math.PI) / 180;
    this._notifyChange();
  }

  /** Horizontal skew in degrees. */
  get skewHDeg(): number {
    if (!this.state) return 0;
    return (Math.atan(this.state.shearX) * 180) / Math.PI;
  }
  set skewHDeg(deg: number) {
    if (!this.state) return;
    this.state.shearX = Math.tan((deg * Math.PI) / 180);
    this._notifyChange();
  }

  /** Vertical skew in degrees. */
  get skewVDeg(): number {
    if (!this.state) return 0;
    return (Math.atan(this.state.shearY) * 180) / Math.PI;
  }
  set skewVDeg(deg: number) {
    if (!this.state) return;
    this.state.shearY = Math.tan((deg * Math.PI) / 180);
    this._notifyChange();
  }

  /**
   * X position of the transformed bbox top-left corner in document space.
   * Reflects srcX + translateX (rotation/shear ignored for the options-bar
   * position display, which matches Photoshop's X/Y fields showing bbox
   * origin when no rotation is applied).
   */
  get posX(): number {
    if (!this.state) return 0;
    return this.state.srcX + this.state.translateX;
  }

  /** Y position of the transformed bbox top-left corner in document space. */
  get posY(): number {
    if (!this.state) return 0;
    return this.state.srcY + this.state.translateY;
  }

  /** Set the X position of the transform's reference-point origin. */
  setPosX(x: number): void {
    if (!this.state) return;
    this.state.translateX = x - this.state.srcX;
    this._notifyChange();
  }

  /** Set the Y position of the transform's reference-point origin. */
  setPosY(y: number): void {
    if (!this.state) return;
    this.state.translateY = y - this.state.srcY;
    this._notifyChange();
  }

  // --- Nudge (arrow-key move) -----------------------------------------------

  /**
   * Move the transform by (dx, dy) pixels. Does NOT commit — the layer's
   * actual x/y is only updated on commit(). Emits a transient preview.
   */
  nudge(ctx: ToolContext, dx: number, dy: number): void {
    if (!this.state) return;
    this.state.translateX += dx;
    this.state.translateY += dy;
    this._notifyChange();
    const cmd = this.buildCommand();
    if (cmd) ctx.emitTransient(cmd);
  }

  // --- Keyboard handling ----------------------------------------------------

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): boolean | void {
    if (!this.state) return;
    // Shift+Arrow nudges by a larger step, matching Photoshop (audit i199).
    const step = modifiers?.shift ? 10 : 1;
    switch (key) {
      case "ArrowLeft":  this.nudge(ctx, -step,  0); return true;
      case "ArrowRight": this.nudge(ctx,  step,  0); return true;
      case "ArrowUp":    this.nudge(ctx,  0, -step); return true;
      case "ArrowDown":  this.nudge(ctx,  0,  step); return true;
      case "Enter":      this.commit(ctx); return true;
      case "Escape":     this.cancel(ctx); return true;
    }
  }

  // --- Double-click commit --------------------------------------------------

  /**
   * Called when the host dispatches a dblclick event. Commits the transform
   * if the click is inside the current bounding box; ignores otherwise.
   */
  onDoubleClick(ctx: ToolContext, screenX: number, screenY: number): void {
    if (!this.state) return;
    const bbox = this.getBoundingBox();
    if (!bbox) return;
    const p = screenToDoc(ctx.viewport, screenX, screenY);
    // Hit-test axis-aligned bbox (rotation ignored for simplicity — matches
    // Photoshop which uses the axis-aligned envelope for the commit hot-zone).
    if (p.x >= bbox.x && p.x <= bbox.x + bbox.w && p.y >= bbox.y && p.y <= bbox.y + bbox.h) {
      this.commit(ctx);
    }
  }

  // --- Public API the host calls -------------------------------------------

  isActive(): boolean {
    return this.state !== null;
  }

  begin(ctx: ToolContext): boolean {
    if (!ctx.doc?.activeLayerId) return false;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return false;
    if (layer.lockAll || layer.lockPosition) return false;
    this.state = {
      layerId: layer.id,
      srcWidth: layer.pixels.width,
      srcHeight: layer.pixels.height,
      srcX: layer.x,
      srcY: layer.y,
      srcBuffer: layer.pixels.clone(),
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
      translateX: 0,
      translateY: 0,
      shearX: 0,
      shearY: 0,
      cornerOffsets: {
        tl: { dx: 0, dy: 0 },
        tr: { dx: 0, dy: 0 },
        bl: { dx: 0, dy: 0 },
        br: { dx: 0, dy: 0 },
      },
      dragging: null,
    };
    return true;
  }

  // Bounding box of the transformed layer in document space, axis-aligned
  // over the four corners. Returned to the renderer to draw transform handles.
  getBoundingBox(): { x: number; y: number; w: number; h: number } | null {
    if (!this.state) return null;
    const corners = this.transformedCorners();
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const c of corners) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  // Oriented bounding box exposing the rotation angle (i106), so the
  // renderer can draw the actual rotated box/handles instead of the
  // axis-aligned envelope getBoundingBox() returns. `x/y/w/h` describe the
  // box in its PRE-ROTATION frame (scale/shear/translate applied, rotation
  // withheld) and `pivotX/pivotY` + `rotation` describe how to place it:
  // the renderer should translate to the pivot, rotate by `rotation`,
  // translate back, then draw the rect — the exact inverse of the
  // un-rotation pickZone()/onPointerDown already do for hit-testing, so the
  // drawn outline and the hit-test region stay in sync.
  //
  // "distort"/"perspective" modes move each corner independently, so there's
  // no single rotation angle; those fall back to rotation: 0 with the same
  // axis-aligned box getBoundingBox() returns (unchanged visual behaviour).
  getOrientedBox(): { x: number; y: number; w: number; h: number; rotation: number; pivotX: number; pivotY: number } | null {
    const frame = this.unrotatedHitFrame();
    if (!frame) return null;
    return {
      x: frame.bbox.x,
      y: frame.bbox.y,
      w: frame.bbox.w,
      h: frame.bbox.h,
      rotation: frame.rotation,
      pivotX: frame.pivotX,
      pivotY: frame.pivotY,
    };
  }

  commit(ctx: ToolContext): void {
    if (!this.state) return;
    const cmd = this.buildCommand();
    this.state = null;
    if (cmd) ctx.emit(cmd);
  }

  cancel(ctx: ToolContext): void {
    if (!this.state) return;
    const s = this.state;
    // Snap parameters back to identity and emit one final transient that
    // restores the pre-transform bbox. We can't rely on the history's undo
    // path here because the transients aren't recorded; instead we feed the
    // command pipeline an identity transform that lands the layer back at
    // the original size and position.
    s.scaleX = 1;
    s.scaleY = 1;
    s.rotation = 0;
    s.translateX = 0;
    s.translateY = 0;
    const cmd = this.buildCommand();
    this.state = null;
    if (cmd) ctx.emitTransient(cmd);
  }

  // --- Pointer handling ----------------------------------------------------

  onPointerDown(ctx: ToolContext, sample: PointerSample): void {
    if (!this.state) return;
    const p = screenToDoc(ctx.viewport, sample.x, sample.y);
    // Hit-test in the box's own PRE-ROTATION frame (i106): un-rotate the
    // pointer around the pivot by the box's current rotation before running
    // the axis-aligned corner/edge distance tests. Testing the raw pointer
    // against the post-rotation AABB (the previous behaviour) works only for
    // rotation ≈ 0/90/180/270°; at any other angle the AABB balloons past the
    // box's actual (rotated) edges and handles become unreachable/mispicked.
    const frame = this.unrotatedHitFrame();
    if (!frame) return;
    const local = unrotatePoint(p.x, p.y, frame.pivotX, frame.pivotY, frame.rotation);
    const zone = pickZone(frame.bbox, local.x, local.y);
    this.state.dragging = {
      zone,
      startDocX: p.x,
      startDocY: p.y,
      startScaleX: this.state.scaleX,
      startScaleY: this.state.scaleY,
      startRotation: this.state.rotation,
      startTranslateX: this.state.translateX,
      startTranslateY: this.state.translateY,
      startShearX: this.state.shearX,
      startShearY: this.state.shearY,
      startCornerOffsets: deepCloneCorners(this.state.cornerOffsets),
      startAltKey: sample.altKey,
    };
  }

  onPointerMove(ctx: ToolContext, sample: PointerSample): void {
    if (!this.state?.dragging) return;
    const p = screenToDoc(ctx.viewport, sample.x, sample.y);
    this.applyDrag(p.x, p.y, sample.shiftKey, sample.altKey);
    this._notifyChange();
    const cmd = this.buildCommand();
    if (cmd) ctx.emitTransient(cmd);
  }

  onPointerUp(_ctx: ToolContext, _sample: PointerSample): void {
    if (!this.state) return;
    this.state.dragging = null;
  }

  // --- Internals -----------------------------------------------------------

  // Delegates to the mode-specific drag handler.
  private applyDrag(curX: number, curY: number, shiftKey: boolean, altKey: boolean): void {
    switch (this.mode) {
      case "free":        return this.applyDragFree(curX, curY, shiftKey, altKey);
      case "scale":       return this.applyDragScale(curX, curY, shiftKey, altKey);
      case "rotate":      return this.applyDragRotate(curX, curY);
      case "skew":        return this.applyDragSkew(curX, curY, shiftKey);
      case "distort":     return this.applyDragDistort(curX, curY);
      case "perspective": return this.applyDragPerspective(curX, curY);
    }
  }

  // Scale: same corner/edge handles as Free, but dragging outside the box
  // does NOT rotate — Photoshop's Transform > Scale restricts the box to
  // resizing only (audit i23).
  private applyDragScale(curX: number, curY: number, shiftKey: boolean, altKey: boolean): void {
    const d = this.state?.dragging;
    if (!d) return;
    if (d.zone === "outside") return; // rotation gesture disabled in Scale mode
    this.applyDragFree(curX, curY, shiftKey, altKey);
  }

  // Rotate: any drag handle (corner, edge, or outside) rotates around the
  // pivot; only an "inside" drag still repositions the layer. Photoshop's
  // Transform > Rotate restricts the box to rotation only (audit i23).
  private applyDragRotate(curX: number, curY: number): void {
    const s = this.state;
    if (!s) return;
    const d = s.dragging;
    if (!d) return;
    if (d.zone === "inside") {
      s.translateX = d.startTranslateX + (curX - d.startDocX);
      s.translateY = d.startTranslateY + (curY - d.startDocY);
      return;
    }
    const rp = this.refPointFraction();
    const cx = s.srcX + s.srcWidth * rp.rx + d.startTranslateX;
    const cy = s.srcY + s.srcHeight * rp.ry + d.startTranslateY;
    const a0 = Math.atan2(d.startDocY - cy, d.startDocX - cx);
    const a1 = Math.atan2(curY - cy, curX - cx);
    s.rotation = d.startRotation + (a1 - a0);
  }

  // Free: scale / rotate / translate (original v1 behaviour).
  private applyDragFree(curX: number, curY: number, shiftKey: boolean, altKey: boolean): void {
    const s = this.state;
    if (!s) return;
    const d = s.dragging;
    if (!d) return;
    const dx = curX - d.startDocX;
    const dy = curY - d.startDocY;
    const rp = this.refPointFraction();
    const cx = s.srcX + s.srcWidth * rp.rx + d.startTranslateX;
    const cy = s.srcY + s.srcHeight * rp.ry + d.startTranslateY;

    switch (d.zone) {
      case "inside": {
        s.translateX = d.startTranslateX + dx;
        s.translateY = d.startTranslateY + dy;
        return;
      }
      case "outside": {
        const a0 = Math.atan2(d.startDocY - cy, d.startDocX - cx);
        const a1 = Math.atan2(curY - cy, curX - cx);
        s.rotation = d.startRotation + (a1 - a0);
        return;
      }
      case "edge-l":
      case "edge-r": {
        const sign = d.zone === "edge-r" ? 1 : -1;
        const w = s.srcWidth * d.startScaleX;
        let newW = Math.max(1, w + sign * dx);
        if (altKey) newW = Math.max(1, w + 2 * sign * dx);
        s.scaleX = (newW / s.srcWidth) || 1e-6;
        if (altKey) s.translateX = d.startTranslateX + (newW - w) * (0.5 - rp.rx) * sign;
        return;
      }
      case "edge-t":
      case "edge-b": {
        const sign = d.zone === "edge-b" ? 1 : -1;
        const h = s.srcHeight * d.startScaleY;
        let newH = Math.max(1, h + sign * dy);
        if (altKey) newH = Math.max(1, h + 2 * sign * dy);
        s.scaleY = (newH / s.srcHeight) || 1e-6;
        if (altKey) s.translateY = d.startTranslateY + (newH - h) * (0.5 - rp.ry) * sign;
        return;
      }
      case "corner-tl":
      case "corner-tr":
      case "corner-bl":
      case "corner-br": {
        const signX = d.zone === "corner-tr" || d.zone === "corner-br" ? 1 : -1;
        const signY = d.zone === "corner-bl" || d.zone === "corner-br" ? 1 : -1;
        const w = s.srcWidth * d.startScaleX;
        const h = s.srcHeight * d.startScaleY;
        let newW = Math.max(1, w + signX * dx);
        let newH = Math.max(1, h + signY * dy);
        // Post-CC2019: aspectLockByDefault=true → plain drag locks, Shift frees.
        // Pre-CC2019: aspectLockByDefault=false → plain drag free, Shift locks.
        const lockAspect = this.aspectLockByDefault ? !shiftKey : shiftKey;
        if (lockAspect) {
          const r = Math.abs(newW / w - 1) > Math.abs(newH / h - 1) ? newW / w : newH / h;
          newW = w * r; newH = h * r;
        }
        if (altKey) {
          newW = Math.max(1, w + 2 * signX * dx);
          newH = Math.max(1, h + 2 * signY * dy);
          if (lockAspect) {
            const r = Math.abs(newW / w - 1) > Math.abs(newH / h - 1) ? newW / w : newH / h;
            newW = w * r; newH = h * r;
          }
          // Alt: scale about the reference point (centre by default) — the
          // pivot buildCommand() already scales around is refX/refY, so no
          // translate compensation is needed there; this term only matters
          // when refPoint isn't dead-centre.
          s.translateX = d.startTranslateX + (newW - w) * (0.5 - rp.rx) * signX;
          s.translateY = d.startTranslateY + (newH - h) * (0.5 - rp.ry) * signY;
        } else {
          // Plain drag (Photoshop default): the OPPOSITE corner stays fixed
          // in document space, regardless of the configured reference point.
          // buildCommand()'s pipeline pivots scale around (refX, refY), so we
          // compensate translateX/Y for the offset between that pivot and the
          // fixed opposite corner:
          //   oppX = signX === 1 ? 0 : srcWidth  (opposite side from the drag)
          //   newTx = startTx - (newSx - startSx) * (oppX - refX)
          // which simplifies (refX = srcWidth * rp.rx) to the term below.
          s.translateX = d.startTranslateX + (newW - w) * (rp.rx - 0.5 + 0.5 * signX);
          s.translateY = d.startTranslateY + (newH - h) * (rp.ry - 0.5 + 0.5 * signY);
        }
        s.scaleX = (newW / s.srcWidth) || 1e-6;
        s.scaleY = (newH / s.srcHeight) || 1e-6;
        return;
      }
    }
  }

  // Skew: shear along edge handles.
  private applyDragSkew(curX: number, curY: number, shiftKey: boolean): void {
    const s = this.state;
    if (!s) return;
    const d = s.dragging;
    if (!d) return;
    const dx = curX - d.startDocX;
    const dy = curY - d.startDocY;
    const scaledH = s.srcHeight * d.startScaleY;
    const scaledW = s.srcWidth * d.startScaleX;

    switch (d.zone) {
      case "edge-l":
      case "edge-r":
        s.shearX = d.startShearX + dy / Math.max(1, scaledH);
        return;
      case "edge-t":
      case "edge-b":
        s.shearY = d.startShearY + dx / Math.max(1, scaledW);
        return;
      case "inside":
        s.translateX = d.startTranslateX + dx;
        s.translateY = d.startTranslateY + dy;
        return;
      case "outside": {
        const rp = this.refPointFraction();
        const cx2 = s.srcX + s.srcWidth * rp.rx + d.startTranslateX;
        const cy2 = s.srcY + s.srcHeight * rp.ry + d.startTranslateY;
        const a0 = Math.atan2(d.startDocY - cy2, d.startDocX - cx2);
        const a1 = Math.atan2(curY - cy2, curX - cx2);
        s.rotation = d.startRotation + (a1 - a0);
        return;
      }
      default: {
        const isCorner = d.zone === "corner-tl" || d.zone === "corner-tr" || d.zone === "corner-bl" || d.zone === "corner-br";
        if (isCorner) {
          const signX = d.zone === "corner-tr" || d.zone === "corner-br" ? 1 : -1;
          const signY = d.zone === "corner-bl" || d.zone === "corner-br" ? 1 : -1;
          let newW = Math.max(1, scaledW + signX * dx);
          let newH = Math.max(1, scaledH + signY * dy);
          if (shiftKey) {
            const r = Math.abs(newW / scaledW - 1) > Math.abs(newH / scaledH - 1) ? newW / scaledW : newH / scaledH;
            newW = scaledW * r; newH = scaledH * r;
          }
          s.scaleX = (newW / s.srcWidth) || 1e-6;
          s.scaleY = (newH / s.srcHeight) || 1e-6;
        }
      }
    }
  }

  // Distort: each corner moves freely.
  private applyDragDistort(curX: number, curY: number): void {
    const s = this.state;
    if (!s) return;
    const d = s.dragging;
    if (!d) return;
    const dx = curX - d.startDocX;
    const dy = curY - d.startDocY;
    const co = s.cornerOffsets;
    const startCo = d.startCornerOffsets;

    switch (d.zone) {
      case "corner-tl": co.tl.dx = startCo.tl.dx + dx; co.tl.dy = startCo.tl.dy + dy; return;
      case "corner-tr": co.tr.dx = startCo.tr.dx + dx; co.tr.dy = startCo.tr.dy + dy; return;
      case "corner-bl": co.bl.dx = startCo.bl.dx + dx; co.bl.dy = startCo.bl.dy + dy; return;
      case "corner-br": co.br.dx = startCo.br.dx + dx; co.br.dy = startCo.br.dy + dy; return;
      case "inside":    s.translateX = d.startTranslateX + dx; s.translateY = d.startTranslateY + dy; return;
      case "edge-t":    co.tl.dy = startCo.tl.dy + dy; co.tr.dy = startCo.tr.dy + dy; return;
      case "edge-b":    co.bl.dy = startCo.bl.dy + dy; co.br.dy = startCo.br.dy + dy; return;
      case "edge-l":    co.tl.dx = startCo.tl.dx + dx; co.bl.dx = startCo.bl.dx + dx; return;
      case "edge-r":    co.tr.dx = startCo.tr.dx + dx; co.br.dx = startCo.br.dx + dx; return;
    }
  }

  // Perspective: opposite-corner symmetric drag.
  private applyDragPerspective(curX: number, curY: number): void {
    const s = this.state;
    if (!s) return;
    const d = s.dragging;
    if (!d) return;
    const dx = curX - d.startDocX;
    const dy = curY - d.startDocY;
    const co = s.cornerOffsets;
    const startCo = d.startCornerOffsets;

    switch (d.zone) {
      case "corner-tl":
        co.tl.dx = startCo.tl.dx + dx; co.tl.dy = startCo.tl.dy + dy;
        co.tr.dx = startCo.tr.dx - dx; co.bl.dy = startCo.bl.dy - dy;
        return;
      case "corner-tr":
        co.tr.dx = startCo.tr.dx + dx; co.tr.dy = startCo.tr.dy + dy;
        co.tl.dx = startCo.tl.dx - dx; co.br.dy = startCo.br.dy - dy;
        return;
      case "corner-bl":
        co.bl.dx = startCo.bl.dx + dx; co.bl.dy = startCo.bl.dy + dy;
        co.br.dx = startCo.br.dx - dx; co.tl.dy = startCo.tl.dy - dy;
        return;
      case "corner-br":
        co.br.dx = startCo.br.dx + dx; co.br.dy = startCo.br.dy + dy;
        co.bl.dx = startCo.bl.dx - dx; co.tr.dy = startCo.tr.dy - dy;
        return;
      case "inside": s.translateX = d.startTranslateX + dx; s.translateY = d.startTranslateY + dy; return;
      case "edge-t":
        co.tl.dy = startCo.tl.dy + dy; co.tr.dy = startCo.tr.dy + dy;
        co.bl.dy = startCo.bl.dy - dy; co.br.dy = startCo.br.dy - dy;
        return;
      case "edge-b":
        co.bl.dy = startCo.bl.dy + dy; co.br.dy = startCo.br.dy + dy;
        co.tl.dy = startCo.tl.dy - dy; co.tr.dy = startCo.tr.dy - dy;
        return;
      case "edge-l":
        co.tl.dx = startCo.tl.dx + dx; co.bl.dx = startCo.bl.dx + dx;
        co.tr.dx = startCo.tr.dx - dx; co.br.dx = startCo.br.dx - dx;
        return;
      case "edge-r":
        co.tr.dx = startCo.tr.dx + dx; co.br.dx = startCo.br.dx + dx;
        co.tl.dx = startCo.tl.dx - dx; co.bl.dx = startCo.bl.dx - dx;
        return;
    }
  }

  private refPointFraction(): { rx: number; ry: number } {
    switch (this.refPoint) {
      case "top-left": return { rx: 0, ry: 0 };
      case "top": return { rx: 0.5, ry: 0 };
      case "top-right": return { rx: 1, ry: 0 };
      case "left": return { rx: 0, ry: 0.5 };
      case "center": return { rx: 0.5, ry: 0.5 };
      case "right": return { rx: 1, ry: 0.5 };
      case "bottom-left": return { rx: 0, ry: 1 };
      case "bottom": return { rx: 0.5, ry: 1 };
      case "bottom-right": return { rx: 1, ry: 1 };
    }
  }

  // Build the TransformLayerCommand from the current parameter set. The
  // matrix maps source-buffer coords (0..srcW, 0..srcH) to the new output
  // buffer's local coords (0..outW, 0..outH).
  //
  // Pivot handling: we rotate/scale around the source rect's centre. That
  // means the layer's "anchor" stays put in document space (modulo the
  // user's drag-translate). Conceptually:
  //
  //     M = T(-bboxMin) ∘ T(docCx) ∘ R(θ) ∘ S(sx, sy) ∘ T(-srcCx) ∘ T(srcOrigin)
  //
  // where:
  //   srcCx, srcCy   = local centre of the source buffer
  //   docCx, docCy   = current centre in doc space (= layer origin + half-size + drag)
  //   bboxMin        = top-left of the rotated/scaled rect's axis-aligned bbox
  //
  // The final layer.x/y is bboxMin.
  private buildCommand(): Command | null {
    if (!this.state) return null;
    const s = this.state;

    // For skew mode we add a shear matrix to the affine pipeline.
    // For distort/perspective modes we fit an affine from the displaced corners.
    if (this.mode === 'distort' || this.mode === 'perspective') {
      return this.buildCommandFromCorners();
    }

    const rp = this.refPointFraction();
    const refX = s.srcWidth * rp.rx;
    const refY = s.srcHeight * rp.ry;
    const m1 = translateMat(-refX, -refY);

    // Scale.
    const m2 = scaleMat(s.scaleX, s.scaleY);

    // Shear (used in skew mode; zero for free mode).
    const mShear: import('../pixel/affineTransform').Affine2D = { a: 1, b: s.shearY, c: s.shearX, d: 1, e: 0, f: 0 };

    // Rotate.
    const m3 = rotateMat(s.rotation);

    // Shift to doc-space reference point + drag.
    const docRx = s.srcX + refX + s.translateX;
    const docRy = s.srcY + refY + s.translateY;
    const m4 = translateMat(docRx, docRy);

    const srcToDoc = compose(m4, compose(m3, compose(mShear, compose(m2, m1))));

    const corners = this.transformedCornersWith(srcToDoc);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of corners) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    const outW = Math.max(1, Math.ceil(maxX - minX));
    const outH = Math.max(1, Math.ceil(maxY - minY));
    const layerX = Math.floor(minX);
    const layerY = Math.floor(minY);
    const m5 = translateMat(-layerX, -layerY);
    const srcToOut = compose(m5, srcToDoc);

    return new TransformLayerCommand(
      s.layerId, srcToOut, outW, outH, layerX, layerY, 'bilinear', this.originalSnapshot(),
    );
  }

  // Build command for distort/perspective modes using corner offsets.
  // We compute an affine best-fit from the 4 source→dest corner pairs.
  private buildCommandFromCorners(): Command | null {
    if (!this.state) return null;
    const s = this.state;
    const rp = this.refPointFraction();
    const refX = s.srcWidth * rp.rx;
    const refY = s.srcHeight * rp.ry;
    const docRx = s.srcX + refX + s.translateX;
    const docRy = s.srcY + refY + s.translateY;

    // The four source corners in local buffer coords.
    const srcCorners = [
      { x: 0,          y: 0 },
      { x: s.srcWidth, y: 0 },
      { x: 0,          y: s.srcHeight },
      { x: s.srcWidth, y: s.srcHeight },
    ];

    // Corresponding doc-space destinations after base affine + corner offsets.
    const baseM = compose(
      translateMat(docRx, docRy),
      compose(rotateMat(s.rotation), compose(scaleMat(s.scaleX, s.scaleY), translateMat(-refX, -refY)))
    );
    const offsets = [s.cornerOffsets.tl, s.cornerOffsets.tr, s.cornerOffsets.bl, s.cornerOffsets.br];
    const dstDocCorners = srcCorners.map((c, i) => {
      const bx = baseM.a * c.x + baseM.c * c.y + baseM.e;
      const by = baseM.b * c.x + baseM.d * c.y + baseM.f;
      return { x: bx + offsets[i]!.dx, y: by + offsets[i]!.dy };
    });

    // Fit an affine matrix from 4 src→dst pairs using least squares.
    const fitM = fitAffine(srcCorners, dstDocCorners);

    // Compute axis-aligned bbox of the 4 dst corners.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of dstDocCorners) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    const outW = Math.max(1, Math.ceil(maxX - minX));
    const outH = Math.max(1, Math.ceil(maxY - minY));
    const layerX = Math.floor(minX);
    const layerY = Math.floor(minY);
    const m5 = translateMat(-layerX, -layerY);
    const srcToOut = compose(m5, fitM);

    return new TransformLayerCommand(
      s.layerId, srcToOut, outW, outH, layerX, layerY, 'bilinear', this.originalSnapshot(),
    );
  }

  // Frozen pre-session layer snapshot passed to every TransformLayerCommand
  // this tool builds (see OriginalLayerSnapshot doc comment) — same buffer
  // reference/original position for every transient + the final commit.
  private originalSnapshot(): OriginalLayerSnapshot | undefined {
    const s = this.state;
    if (!s) return undefined;
    return { pixels: s.srcBuffer, x: s.srcX, y: s.srcY };
  }

  // Corners of the source rect transformed into doc space using the current
  // parameter values. Used by getBoundingBox().
  private transformedCorners(): { x: number; y: number }[] {
    const s = this.state;
    if (!s) return [];

    if (this.mode === 'distort' || this.mode === 'perspective') {
      // Return the actual displaced corners.
      const rp = this.refPointFraction();
      const refX = s.srcWidth * rp.rx;
      const refY = s.srcHeight * rp.ry;
      const docRx = s.srcX + refX + s.translateX;
      const docRy = s.srcY + refY + s.translateY;
      const baseM = compose(
        translateMat(docRx, docRy),
        compose(rotateMat(s.rotation), compose(scaleMat(s.scaleX, s.scaleY), translateMat(-refX, -refY)))
      );
      const srcCorners = [
        { x: 0, y: 0 }, { x: s.srcWidth, y: 0 },
        { x: 0, y: s.srcHeight }, { x: s.srcWidth, y: s.srcHeight },
      ];
      const offsets = [s.cornerOffsets.tl, s.cornerOffsets.tr, s.cornerOffsets.bl, s.cornerOffsets.br];
      return srcCorners.map((c, i) => ({
        x: baseM.a * c.x + baseM.c * c.y + baseM.e + offsets[i]!.dx,
        y: baseM.b * c.x + baseM.d * c.y + baseM.f + offsets[i]!.dy,
      }));
    }

    const rp = this.refPointFraction();
    const refX = s.srcWidth * rp.rx;
    const refY = s.srcHeight * rp.ry;
    const docRx = s.srcX + refX + s.translateX;
    const docRy = s.srcY + refY + s.translateY;
    const mShear: import('../pixel/affineTransform').Affine2D = { a: 1, b: s.shearY, c: s.shearX, d: 1, e: 0, f: 0 };
    const m = compose(
      translateMat(docRx, docRy),
      compose(rotateMat(s.rotation), compose(mShear, compose(scaleMat(s.scaleX, s.scaleY), translateMat(-refX, -refY)))),
    );
    return this.transformedCornersWith(m);
  }

  // Box shape + pivot BEFORE rotation is applied, plus the rotation angle
  // itself — used to hit-test a rotated box by un-rotating the pointer into
  // this local frame (i106). For "free"/"scale"/"rotate" (and "skew", whose
  // shear is orthogonal to rotation) the box is still a simple rect at this
  // stage: scale + shear applied, rotate withheld. "distort"/"perspective"
  // move each corner independently — there's no single rotation angle that
  // describes them, so those modes fall back to the plain post-transform
  // AABB (unchanged behaviour; same as before this fix).
  private unrotatedHitFrame(): { bbox: { x: number; y: number; w: number; h: number }; pivotX: number; pivotY: number; rotation: number } | null {
    const s = this.state;
    if (!s) return null;
    const rp = this.refPointFraction();
    const refX = s.srcWidth * rp.rx;
    const refY = s.srcHeight * rp.ry;
    const docRx = s.srcX + refX + s.translateX;
    const docRy = s.srcY + refY + s.translateY;

    if (this.mode === "distort" || this.mode === "perspective") {
      const bbox = this.getBoundingBox();
      return bbox ? { bbox, pivotX: docRx, pivotY: docRy, rotation: 0 } : null;
    }

    const mShear: Affine2D = { a: 1, b: s.shearY, c: s.shearX, d: 1, e: 0, f: 0 };
    const m = compose(
      translateMat(docRx, docRy),
      compose(mShear, compose(scaleMat(s.scaleX, s.scaleY), translateMat(-refX, -refY))),
    );
    const corners = this.transformedCornersWith(m);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of corners) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    return {
      bbox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
      pivotX: docRx,
      pivotY: docRy,
      rotation: s.rotation,
    };
  }

  private transformedCornersWith(m: Affine2D): { x: number; y: number }[] {
    const s = this.state;
    if (!s) return [];
    const w = s.srcWidth;
    const h = s.srcHeight;
    const tx = (px: number, py: number) => ({
      x: m.a * px + m.c * py + m.e,
      y: m.b * px + m.d * py + m.f,
    });
    return [tx(0, 0), tx(w, 0), tx(0, h), tx(w, h)];
  }
}


// Deep-clone corner offsets for drag bookkeeping.
function deepCloneCorners(co: CornerOffsets): CornerOffsets {
  return {
    tl: { dx: co.tl.dx, dy: co.tl.dy },
    tr: { dx: co.tr.dx, dy: co.tr.dy },
    bl: { dx: co.bl.dx, dy: co.bl.dy },
    br: { dx: co.br.dx, dy: co.br.dy },
  };
}

/**
 * Fit an affine transform from 4 source points to 4 destination points
 * using a least-squares 6-parameter solve.
 * Returns the best-fit Affine2D mapping src → dst.
 */
function fitAffine(
  src: { x: number; y: number }[],
  dst: { x: number; y: number }[],
): Affine2D {
  // Solve the 6-parameter affine [a c e; b d f] via two independent
  // 3-parameter least-squares problems (one for x, one for y).
  // For each point pair: x' = a*sx + c*sy + e
  //                       y' = b*sx + d*sy + f
  const n = src.length;
  if (n < 3) return identity();
  // Normal equations for x-row: [a c e]
  let sumSx2 = 0, sumSxSy = 0, sumSy2 = 0, sumSx = 0, sumSy = 0;
  let sumSxDx = 0, sumSyDx = 0, sumDx = 0;
  let sumSxDy = 0, sumSyDy = 0, sumDy = 0;
  for (let i = 0; i < n; i++) {
    const { x: sx, y: sy } = src[i]!;
    const { x: dx, y: dy } = dst[i]!;
    sumSx2 += sx * sx; sumSxSy += sx * sy; sumSy2 += sy * sy;
    sumSx += sx; sumSy += sy;
    sumSxDx += sx * dx; sumSyDx += sy * dx; sumDx += dx;
    sumSxDy += sx * dy; sumSyDy += sy * dy; sumDy += dy;
  }
  // Solve 3x3 normal system via Cramer's rule.
  const M = [[sumSx2, sumSxSy, sumSx], [sumSxSy, sumSy2, sumSy], [sumSx, sumSy, n]];
  const bX = [sumSxDx, sumSyDx, sumDx];
  const bY = [sumSxDy, sumSyDy, sumDy];
  const xParams = solve3(M as number[][], bX);
  const yParams = solve3(M as number[][], bY);
  if (!xParams || !yParams) return identity();
  return { a: xParams[0]!, b: yParams[0]!, c: xParams[1]!, d: yParams[1]!, e: xParams[2]!, f: yParams[2]! };
}

/** Solve a 3x3 linear system Ax=b. Returns null if singular. */
function solve3(A: number[][], b: number[]): number[] | null {
  // Augmented matrix.
  const M = A.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < 3; col++) {
    let maxRow = col, maxVal = Math.abs(M[col]![col]!);
    for (let row = col + 1; row < 3; row++) {
      const v = Math.abs(M[row]![col]!);
      if (v > maxVal) { maxVal = v; maxRow = row; }
    }
    if (maxVal < 1e-12) return null;
    [M[col], M[maxRow]] = [M[maxRow]!, M[col]!];
    const pivot = M[col]![col]!;
    for (let row = col + 1; row < 3; row++) {
      const f = M[row]![col]! / pivot;
      for (let k = col; k <= 3; k++) M[row]![k]! -= f * M[col]![k]!;
    }
  }
  const x = [0, 0, 0];
  for (let i = 2; i >= 0; i--) {
    let sum = M[i]![3]!;
    for (let j = i + 1; j < 3; j++) sum -= M[i]![j]! * x[j]!;
    x[i] = sum / M[i]![i]!;
  }
  return x;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Hit-testing zones around the layer's axis-aligned bounding box. The
// thresholds are intentionally loose for v1 — a precise hit test would
// account for the rotated rect rather than its axis-aligned envelope.
const EDGE_TOL = 6; // doc-space px tolerance for edge handles
const OUTSIDE_TOL = 12; // beyond this band outside the bbox, treat as rotate

/**
 * Rotate a document-space point by -rotation around (pivotX, pivotY),
 * mapping it into the box's local (pre-rotation) frame. Standard technique
 * for hit-testing an oriented rectangle: un-rotate the query point instead
 * of rotating the rectangle (i106).
 */
function unrotatePoint(
  x: number,
  y: number,
  pivotX: number,
  pivotY: number,
  rotation: number,
): { x: number; y: number } {
  if (rotation === 0) return { x, y };
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const relX = x - pivotX;
  const relY = y - pivotY;
  return {
    x: pivotX + cos * relX + sin * relY,
    y: pivotY - sin * relX + cos * relY,
  };
}

function pickZone(
  bbox: { x: number; y: number; w: number; h: number },
  x: number,
  y: number,
): HandleZone {
  const left = bbox.x;
  const right = bbox.x + bbox.w;
  const top = bbox.y;
  const bottom = bbox.y + bbox.h;

  const nearL = Math.abs(x - left) <= EDGE_TOL;
  const nearR = Math.abs(x - right) <= EDGE_TOL;
  const nearT = Math.abs(y - top) <= EDGE_TOL;
  const nearB = Math.abs(y - bottom) <= EDGE_TOL;

  if (nearL && nearT) return "corner-tl";
  if (nearR && nearT) return "corner-tr";
  if (nearL && nearB) return "corner-bl";
  if (nearR && nearB) return "corner-br";

  const insideX = x >= left - EDGE_TOL && x <= right + EDGE_TOL;
  const insideY = y >= top - EDGE_TOL && y <= bottom + EDGE_TOL;
  if (nearL && insideY) return "edge-l";
  if (nearR && insideY) return "edge-r";
  if (nearT && insideX) return "edge-t";
  if (nearB && insideX) return "edge-b";

  if (x >= left && x <= right && y >= top && y <= bottom) return "inside";

  // Outside the bbox (beyond the edge tolerance) → rotation zone.
  if (
    x < left - OUTSIDE_TOL ||
    x > right + OUTSIDE_TOL ||
    y < top - OUTSIDE_TOL ||
    y > bottom + OUTSIDE_TOL
  ) {
    return "outside";
  }
  return "inside";
}
